// DrawerFormHost — switch component that picks the right drawer-aware
// form based on URL `?form=<kind>` param. Rendered by the AI 導覽
// Topology page inside AiConsoleDrawer's "form" view.
//
// Spec docs/specs/ai-mode-operations-console.md §5.2 — AM-7 drawer
// forms are AI-emitted only (no operator entry path; the IconRow has
// no「open form」button). When AI emits `propose_drawer_form`,
// ChatBubble navigates to `/dashboard/topology?drawer=form&form=
// <kind>&form_call_id=<id>&form_prefill=<json>` and this component
// reads the URL to render the right form.
//
// Closing the form (save success OR cancel) clears the `?form=` +
// `?form_call_id=` params AND posts a tool_result back to the AI
// thread so the chain stays consistent — symmetric to PlanPanel's
// `attemptClose` → handlePlanClose → sendAiToolResults flow.

import React, { useMemo } from "react";
import DrawerCreateLocationForm from "./DrawerCreateLocationForm.js";
import DrawerCreateClientForm from "./DrawerCreateClientForm.js";
import DrawerCreateTaskForm from "./DrawerCreateTaskForm.js";
import DrawerIssueBindingCodeForm from "./DrawerIssueBindingCodeForm.js";
import type { DrawerFormKind } from "../lib/chatApi.js";
import type { IssuedCode } from "../components/BindingCodeIssueModal.js";

export interface DrawerFormHostProps {
  formKind: DrawerFormKind | null;
  formCallId: string | null;
  prefillJson: string | null;
  reason: string | null;
  tenantId: string | null;
  /** Fires when the form completes (save or cancel). The host page is
   *  expected to: (1) clear `?form=` + `?form_call_id=` + `?form_prefill=`
   *  from URL, (2) post the tool_result back to AI via the chat thread,
   *  (3) optionally switch drawer view to topology if the entity was
   *  created so the new node surfaces visually. */
  onComplete: (outcome: DrawerFormOutcome) => void;
}

export type DrawerFormOutcome =
  | { kind: "saved"; entity_id: string; entity_kind: DrawerFormKind }
  /** issue_binding_code 專用：8 字碼 + redeem_url 等敏感資料不能送 OpenAI。
   *  ChatBubble 收到後在本地（non-persisted）插入卡片給 operator 看。
   *  tool_result 回 AI 只給 `{accepted:true, kind:"issue_binding_code"}`。 */
  | { kind: "issued_binding_code"; issued: IssuedCode }
  | { kind: "cancelled"; reason: "user_cancelled" | "form_unsupported" };

export default function DrawerFormHost({
  formKind, formCallId, prefillJson, reason, tenantId, onComplete,
}: DrawerFormHostProps): React.ReactElement {
  const prefill = useMemo<Record<string, unknown>>(() => {
    if (!prefillJson) return {};
    try { return JSON.parse(prefillJson) as Record<string, unknown>; }
    catch { return {}; }
  }, [prefillJson]);

  if (!formKind || !formCallId) {
    return (
      <div className="p-4 text-xs text-text-muted leading-relaxed">
        目前沒有等待填寫的表單。AI 提出新表單時，這裡會自動顯示。
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {reason && (
        <div className="px-4 py-2 border-b border-border-subtle bg-bg-raised text-2xs text-text-secondary leading-relaxed flex-shrink-0">
          <span className="font-medium text-accent">AI 建議：</span>{reason}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {formKind === "create_location" && (
          <DrawerCreateLocationForm
            prefill={prefill}
            tenantId={tenantId}
            onSaved={(id) => onComplete({ kind: "saved", entity_id: id, entity_kind: "create_location" })}
            onCancel={() => onComplete({ kind: "cancelled", reason: "user_cancelled" })}
          />
        )}
        {formKind === "create_client" && (
          <DrawerCreateClientForm
            prefill={prefill}
            tenantId={tenantId}
            onSaved={(id) => onComplete({ kind: "saved", entity_id: id, entity_kind: "create_client" })}
            onCancel={() => onComplete({ kind: "cancelled", reason: "user_cancelled" })}
          />
        )}
        {formKind === "create_task" && (
          <DrawerCreateTaskForm
            prefill={prefill}
            tenantId={tenantId}
            onSaved={(id) => onComplete({ kind: "saved", entity_id: id, entity_kind: "create_task" })}
            onCancel={() => onComplete({ kind: "cancelled", reason: "user_cancelled" })}
          />
        )}
        {formKind === "issue_binding_code" && (
          <DrawerIssueBindingCodeForm
            prefill={prefill}
            onSaved={(issued) => onComplete({ kind: "issued_binding_code", issued })}
            onCancel={() => onComplete({ kind: "cancelled", reason: "user_cancelled" })}
          />
        )}
        {formKind !== "create_location" && formKind !== "create_client" && formKind !== "create_task" && formKind !== "issue_binding_code" && (
          // Defensive — `formKind` is typed but the AI might emit a
          // newer kind the client hasn't shipped yet. Treat as
          // cancellation so AI doesn't hang waiting for a tool_result.
          <UnsupportedFormFallback onAck={() => onComplete({ kind: "cancelled", reason: "form_unsupported" })} />
        )}
      </div>
    </div>
  );
}

function UnsupportedFormFallback({ onAck }: { onAck: () => void }): React.ReactElement {
  return (
    <div className="space-y-3 text-sm text-text-secondary">
      <p>這個表單類型目前不支援。請回到對話請 AI 改用其他方式（例如直接套用計畫或帶您到設定頁）。</p>
      <button type="button" onClick={onAck} className="btn-secondary text-sm">關閉</button>
    </div>
  );
}
